import { autoinject, bindable, computedFrom, Disposable, observable } from "aurelia-framework";
import { LookupClientService } from "../../../services/lookups/lookup-client.service";
import { LookupValueRequest } from "../../../models/lookups/lookup.models";
import { I18N } from "aurelia-i18n";
import { Reflection } from "../../../utilities/reflection";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { EventAggregator } from "aurelia-event-aggregator";
import { Messages } from "../../../configuration/event-messages.constants";
import * as Enumerable from "linq";
import { ListDisplayHelper } from "../../../helpers/list-display-helper";

@autoinject
export class CheckedListSelector {

    private count: number = 0;
    public orderedItems: any[];

    @bindable
    public items: any[];

    @bindable
    public uri: string = "";

    @bindable
    public lookupParam: string = "";

    @bindable
    public propertyName: string = "";

    @bindable
    public selectedItemIds: number[] = [];

    @bindable
    public externalLabel: string;

    @bindable
    public mobileTitle: string;

    @bindable
    public mobileDescription: string;

    @bindable
    public excludeItemsById: number[];

    @bindable
    public onlyIncludeItemsById: number[];

    @bindable
    public sortAlphabetically: boolean = true;

    @bindable
    public showActionButtons: boolean = true;

    @bindable
    public dropdownAlignedRight: boolean;

    @bindable
    public allowSelectAll: boolean = false;

    @bindable()
    public nameConverterFunc: ((x) => string);

    @bindable()
    public displayMemberPath: string | ((item) => string);

    @observable()
    public selectAll: boolean = false;

    @bindable
    public refreshDataOnClear: boolean = true;

    constructor(private lookupsClientService: LookupClientService, private element: Element, private i18N: I18N,
        private eventAggregator: EventAggregator) { }

    @computedFrom(Reflection.getPropertyPath<CheckedListSelector>(x => x.propertyName))
    public get noItemsPlaceholder() {
        return this.i18N.tr('no-custom-named-items-available').replace("{0}", this.propertyName);
    }

    propertyNameChanged() {
        this.updateExternalLabel();
    }

    previousItems: any[] = [];

    onlyIncludeItemsByIdChanged() {
        this.itemsChanged();
    }

    private orderItems() {
        this.orderedItems = this.items ? Enumerable.from(this.items).orderByDescending(x => x.isSelected).toArray() : [];
    }

    itemsChanged() {
        if (this.items) {

            if (this.onlyIncludeItemsById && this.onlyIncludeItemsById.length > 0) {
                const excludeItemsIds = Enumerable.from(this.items.filter(x => this.onlyIncludeItemsById.indexOf(x.id) == -1).map(x => x.id)).distinct().toArray();

                if (excludeItemsIds.length > 0) {

                    if (!this.excludeItemsById) this.excludeItemsById = [];

                    this.excludeItemsById = this.excludeItemsById.concat(excludeItemsIds);
                }
            }

            if (this.excludeItemsById && this.excludeItemsById.length > 0) {

                //get index of items to be removed, descending order to remove from back so indexes would not change as items are removed
                const exludeIndexes = Enumerable.from(this.items.map((x, i) => this.excludeItemsById.indexOf(x.id) > -1 ? i : null).filter(x => x != null)).orderByDescending(x => x).toArray();

                exludeIndexes.forEach(i => {
                    this.items.splice(i, 1);
                });
            }
            if (this.selectedItemIds) {
                this.updateItemsWithSelectedIds()
                this.updateExternalLabel();
                this.orderItems();
            }
            else if (!this.orderedItems || this.orderedItems.length == 0) this.orderItems();
        }
    }

    updateExternalLabel() {
        this.externalLabel = `${this.propertyName}`;
        this.mobileTitle = this.externalLabel;
        this.mobileDescription = ListDisplayHelper.getSelectableListToString(this.items);
    }

    switchItemSelection() {
        if (!this.selectedItemIds) this.selectedItemIds = [];

        this.selectedItemIds = this.items.filter(x => x.isSelected).map(x => x.id);

        this.updateExternalLabel();
    }

    lookupParamChanged() {
        if (this.lookupParam) {
            this.uriChanged();
        }
    }

    uriChanged() {
        if (this.uri && this.isAttached) {

            this.count++;
            const localCount = this.count;

            const request = new LookupValueRequest();
            request.lookupValueQuery = this.uri;

            if (this.lookupParam) {
                request.parameters = this.lookupParam;
            }

            this.lookupsClientService.getLookup(request, 'default').then(x => {
                if (localCount !== this.count) {
                    return;
                }

                this.items = CloneHelper.cloneObject(x.activeValues) ?? [];

                if (this.selectedItemIds) {
                    this.items.filter(y => this.selectedItemIds.includes(y.id)).forEach(y => y.isSelected = true);
                }
                if (this.sortAlphabetically)
                    this.items = this.items.sort((a, b) => {
                        if (a.name > b.name) return 1;
                        if (a.name < b.name) return -1;
                        return 0;
                    });

                this.previousItems = CloneHelper.cloneObject(this.items);
            })
        } else {
            this.items = [];
        }
    }

    selectedItemIdsChanged() {

        if (this.items) {
            if (!this.selectedItemIds || this.selectedItemIds.length == 0) {
                this.items.forEach(x => x["isSelected"] = false);
            } else {
                this.updateItemsWithSelectedIds()
            }
        }
        this.updateExternalLabel();
    }

    selectAllChanged() {
        if (this.items && this.allowSelectAll == true) {
            this.selectedItemIds = [];
            if (this.selectAll) {
                this.items.forEach(x => {
                    x.isSelected = true;
                    if (this.selectedItemIds.indexOf(x.id) == -1) {
                        this.selectedItemIds.push(x.id);
                    }
                });
            }
            else this.items.forEach(x => x.isSelected = false);
        }
    }

    public clear() {
        this.selectAll = false;
        if (this.items) {
            this.items.forEach(x => x.isSelected = false);
        }
        this.selectedItemIds = [];
        this.updateExternalLabel();

        if (this.refreshDataOnClear) {
            setTimeout(() => {
                this.applyFilters(false);
            }, 120);
        }
    }

    public applyFilters(closeFilter: boolean = true, reset: boolean = false) {
        if (reset) {
            this.items = this.previousItems;
            this.items.forEach(x => {
                if (x.isSelected) {
                    if (this.selectedItemIds.indexOf(x.id) == -1) {
                        this.selectedItemIds.push(x.id);
                    }
                }
            });
        }
        const changeEvent: CustomEvent = new CustomEvent("apply-filters", { bubbles: true, detail: { closeFilter: closeFilter, filters: this.items ? this.items.filter(x => x.isSelected) : [], items: this.items } });
        this.isOpen = !closeFilter
        this.element.dispatchEvent(changeEvent);
        return true;
    }


    _subscriptionToDocumentClickEvent: Disposable;
    isAttached: boolean;
    attached() {
        this.isAttached = true;
        setTimeout(() => {
            this.isOpen = true;
            this.previousItems = CloneHelper.cloneObject(this.items);
            this._subscriptionToDocumentClickEvent = this.eventAggregator.subscribe(Messages.documentClick, this.closePopup.bind(this));

            this.orderItems();
        });
        if (this.uri)
            this.uriChanged();
    }

    detached() {
        this.isAttached = false;
        if (this._subscriptionToDocumentClickEvent)
            this._subscriptionToDocumentClickEvent.dispose();
    }

    isOpen: boolean;
    closePopup(event: any) {
        if (event?.source == 'document' && this.isOpen) {
            this.isOpen = false;
            this.applyFilters(true, true);
        }
    }

    nameConverter(item: any) {
        if (!item) {
            return null;
        }
        if (this.displayMemberPath) {
            if (typeof this.displayMemberPath == "function") {
                return this.displayMemberPath(item)
            } else {
                return item[this.displayMemberPath];
            }
        }

        if (this.nameConverterFunc) {
            return this.nameConverterFunc(item.name);
        }

        return item.name;
    }

    updateItemsWithSelectedIds() {
        // I don't want to update items IF selected item are not of type number i.e. NOT an ID
        this.selectedItemIds.forEach(selectedItem => {
            if (typeof selectedItem == "number") {
                const itemMatchedWithSelected = this.items.filter(x => x.id == selectedItem)[0];
                if (itemMatchedWithSelected) itemMatchedWithSelected.isSelected = true;
            }
        });
    }
}
